import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { X } from 'lucide-react';

export default function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a short delay
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const consent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-40" />

      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
        <Card className="max-w-4xl mx-auto bg-white shadow-2xl border-2 border-orange-500">
          <div className="p-6 md:p-8">
            {!showSettings ? (
              <>
                {/* Main Banner Content */}
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                      🍪 Aviso de Privacidad y Cookies
                    </h2>
                    <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-4">
                      Utilizamos cookies propias y de terceros para mejorar nuestros servicios, 
                      analizar el tráfico web y facilitar tu experiencia de navegación. Algunas cookies 
                      son necesarias para el funcionamiento básico de la web, mientras que otras nos 
                      ayudan a mejorar nuestros servicios.
                    </p>
                    <p className="text-sm text-gray-600">
                      Puedes aceptar todas las cookies, rechazar las opcionales o configurar tus preferencias. 
                      Para más información, consulta nuestra{' '}
                      <a 
                        href="/privacy" 
                        className="text-orange-600 hover:text-orange-700 font-semibold underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Política de Privacidad
                      </a>.
                    </p>
                  </div>
                  <button
                    onClick={handleRejectAll}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Cerrar"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleAcceptAll}
                    className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3"
                  >
                    Aceptar Todas
                  </Button>
                  <Button
                    onClick={handleRejectAll}
                    variant="outline"
                    className="flex-1 border-2 border-gray-300 hover:bg-gray-100 font-semibold py-3"
                  >
                    Rechazar Opcionales
                  </Button>
                  <Button
                    onClick={() => setShowSettings(true)}
                    variant="outline"
                    className="flex-1 border-2 border-orange-500 text-orange-600 hover:bg-orange-50 font-semibold py-3"
                  >
                    Configurar
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Settings Panel */}
                <div className="mb-6">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                    Configuración de Cookies
                  </h2>
                  <p className="text-sm text-gray-600 mb-6">
                    Gestiona tus preferencias de cookies. Las cookies necesarias no se pueden desactivar.
                  </p>

                  <div className="space-y-4">
                    {/* Necessary Cookies */}
                    <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">
                          Cookies Necesarias
                        </h3>
                        <p className="text-sm text-gray-600">
                          Esenciales para el funcionamiento básico de la web (autenticación, sesión, seguridad).
                        </p>
                      </div>
                      <div className="ml-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                          Siempre activas
                        </span>
                      </div>
                    </div>

                    {/* Analytics Cookies */}
                    <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">
                          Cookies Analíticas
                        </h3>
                        <p className="text-sm text-gray-600">
                          Nos ayudan a entender cómo los visitantes interactúan con la web.
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-4">
                        <input
                          type="checkbox"
                          checked={preferences.analytics}
                          onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                      </label>
                    </div>

                    {/* Marketing Cookies */}
                    <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">
                          Cookies de Marketing
                        </h3>
                        <p className="text-sm text-gray-600">
                          Se utilizan para rastrear visitantes y mostrar anuncios más relevantes.
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-4">
                        <input
                          type="checkbox"
                          checked={preferences.marketing}
                          onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Settings Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleSavePreferences}
                    className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3"
                  >
                    Guardar Preferencias
                  </Button>
                  <Button
                    onClick={() => setShowSettings(false)}
                    variant="outline"
                    className="flex-1 border-2 border-gray-300 hover:bg-gray-100 font-semibold py-3"
                  >
                    Volver
                  </Button>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
    </>
  );
}
